import { fromDayKey, toDayKey } from "./schedule";

/**
 * Habit reminders — pure logic shared by the notification adapters
 * (web foreground / Tauri OS scheduler).
 *
 * A `habit_reminder` is recurring ("every day at HH:MM"). OS backends schedule
 * ONE occurrence (the next one); they re-synchronize on every startup / data
 * change, so every enabled reminder always has a future occurrence registered.
 */

/** Reminder registered with the OS (or in the adapter's local registry). */
export interface ScheduledReminder {
  /** Notification backend identifier (numeric in Tauri → string). */
  id: string;
  /** ID of the habit_reminder row. */
  reminderId: string;
  /**
   * ISO instant for which it was scheduled. If it no longer matches the next
   * calculated occurrence, it is rescheduled (the time changed or the occurrence passed).
   */
  fireAt?: string | null;
}

/** Scheduler entry: enabled reminder + data for the content. */
export interface HabitReminderTarget {
  id: string;
  habitId: string;
  habitName: string;
  /** "HH:MM" local time. */
  timeOfDay: string;
}

export interface ReminderDiff {
  toSchedule: HabitReminderTarget[];
  toCancel: string[]; // identificadores del OS
}

export const MAX_SCHEDULED_REMINDERS = 60;

const FNV_OFFSET_BASIS = 2_166_136_261;
const FNV_PRIME = 16_777_619;
const INT32_MAX = 2_147_483_647;

/**
 * Maps a reminder UUID to a stable positive 31-bit integer.
 * Tauri `plugin-notification` indexes by `i32`; deriving it from the id
 * avoids duplicate scheduling even if the local registry is lost.
 */
export function reminderNumericId(reminderId: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < reminderId.length; i += 1) {
    // FNV-1a: XOR the next code unit and multiply by the prime.
    // oxlint-disable-next-line eslint/no-bitwise
    hash ^= reminderId.codePointAt(i) ?? 0;
    hash = Math.imul(hash, FNV_PRIME);
  }
  return ((hash % INT32_MAX) + INT32_MAX) % INT32_MAX;
}

/** Next strictly-future local occurrence of "HH:MM" relative to `from`. */
export function nextFireTimeISO(timeOfDay: string, from: Date = new Date()): string {
  const [h = 0, m = 0] = timeOfDay.split(":").map(Number);
  const candidate = fromDayKey(toDayKey(from));
  candidate.setHours(h, m, 0, 0);
  if (candidate.getTime() <= from.getTime()) {
    candidate.setDate(candidate.getDate() + 1);
  }
  return candidate.toISOString();
}

function reminderTimeChanged(
  scheduled: ScheduledReminder,
  target: HabitReminderTarget,
  now: Date,
): boolean {
  if (scheduled.fireAt === undefined || scheduled.fireAt === null) {
    return false;
  }
  // Reschedule if the registered occurrence is in the past or the time of day
  // no longer matches the next calculated occurrence.
  const fireAt = new Date(scheduled.fireAt);
  if (Number.isNaN(fireAt.getTime()) || fireAt.getTime() <= now.getTime()) {
    return true;
  }
  return scheduled.fireAt !== nextFireTimeISO(target.timeOfDay, now);
}

/** Diff between what is registered with the OS and the desired state. */
export function calculateReminderDiff(
  targets: HabitReminderTarget[],
  currentlyScheduled: ScheduledReminder[],
  maxNotifications = MAX_SCHEDULED_REMINDERS,
  now: Date = new Date(),
): ReminderDiff {
  const scheduledMap = new Map(currentlyScheduled.map((n) => [n.reminderId, n]));
  const wanted = new Set(targets.slice(0, maxNotifications).map((t) => t.id));

  const toSchedule: HabitReminderTarget[] = [];
  const toCancel: string[] = [];

  for (const target of targets) {
    const isNeeded = wanted.has(target.id);
    const scheduled = scheduledMap.get(target.id);

    if (isNeeded && !scheduled) {
      toSchedule.push(target);
    } else if (isNeeded && scheduled && reminderTimeChanged(scheduled, target, now)) {
      toCancel.push(scheduled.id);
      toSchedule.push(target);
    } else if (!isNeeded && scheduled) {
      toCancel.push(scheduled.id);
    }

    if (scheduled) {
      scheduledMap.delete(target.id);
    }
  }

  // Orphans (deleted/disabled reminder) → cancel.
  for (const scheduled of scheduledMap.values()) {
    toCancel.push(scheduled.id);
  }

  return { toSchedule, toCancel };
}

/** Applies the diff to the OS backend. */
export async function syncReminders(options: {
  targets: HabitReminderTarget[];
  currentlyScheduled: ScheduledReminder[];
  schedule: (target: HabitReminderTarget) => Promise<void>;
  cancel: (osId: string) => Promise<void>;
  maxNotifications?: number;
}): Promise<void> {
  const diff = calculateReminderDiff(
    options.targets,
    options.currentlyScheduled,
    options.maxNotifications ?? MAX_SCHEDULED_REMINDERS,
  );
  for (const osId of diff.toCancel) {
    await options.cancel(osId);
  }
  for (const target of diff.toSchedule) {
    await options.schedule(target);
  }
}

/**
 * Window after the target time during which notifying still makes sense.
 *
 * This is the **code default** for `reminders.foreground_fire_window_min`
 * (CONFIG_OVER_CODE): an exact mirror of `builtin_defaults()` in
 * `packages/core-rust/src/app_config/resolve.rs`. Without any configuration,
 * the behavior is the historical one.
 */
export const FOREGROUND_FIRE_WINDOW_MIN = 60;

/**
 * Web path (without an OS scheduler): fires when local time crosses HH:MM
 * (with a grace window — opening the app hours later no longer notifies).
 * `notifiedKeys` prevents duplicates (key reminderId:localDay).
 *
 * `windowMin` comes from `GET /api/config` (the admin can narrow or widen it
 * at runtime); without resolved config, the code default is used.
 */
export function checkDueForegroundReminders(options: {
  targets: HabitReminderTarget[];
  notifiedKeys: Set<string>;
  onNotify: (target: HabitReminderTarget) => void;
  windowMin?: number;
  now?: Date;
}): void {
  const now = options.now ?? new Date();
  const windowMin = options.windowMin ?? FOREGROUND_FIRE_WINDOW_MIN;
  const dayKey = toDayKey(now);
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const MINUTES_PER_HOUR = 60;
  for (const target of options.targets) {
    const key = `${target.id}:${dayKey}`;
    if (options.notifiedKeys.has(key)) {
      continue;
    }
    const [h = 0, m = 0] = target.timeOfDay.split(":").map(Number);
    const minutesTarget = h * MINUTES_PER_HOUR + m;
    const delta = minutesNow - minutesTarget;
    if (delta >= 0 && delta <= windowMin) {
      options.notifiedKeys.add(key);
      options.onNotify(target);
    }
  }
}
